import React, { useCallback, useEffect, useState } from "react";
import styles from "./OrdersTable.module.sass";
import Row from "./Row";
import { fetchOrders } from "../../../API/Orders";
import { ConfigProvider, Pagination } from "antd";
import CustomToast from "../../../components/CustomToast";
import Card from "../../../components/Card";
import cn from "classnames";
import useDarkMode from "use-dark-mode";
import Checkbox from "../../../components/Checkbox";

const OrdersTable = ({ duration, onOrdersImported, onSelectedOrdersChange }) => {
  const customOrders = "CUSTOM";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedPageSize = localStorage.getItem("itemsPerPage");
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = [
        { key: "page", value: page },
        { key: "pageSize", value: itemsPerPage },
        { key: "duration", value: "DEFAULT" },
        { key: "order_type", value: customOrders },
      ];

      const response = await fetchOrders(queryParams);
      setData(response.data);
      setTotalItems(response.count);
      
      if (response.data.length > 0) {
        const currentPageSelected = response.data.every(item => 
          selectedOrders.has(item.id)
        );
        setSelectAll(currentPageSelected);
      }
    } catch (error) {
      CustomToast.fire({
        icon: "error",
        text: "Error fetching data",
      });
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, duration, selectedOrders]);

  useEffect(() => {
    fetchData();
  }, [fetchData, onOrdersImported]);

  useEffect(() => {
    onSelectedOrdersChange(Array.from(selectedOrders));
  }, [selectedOrders, onSelectedOrdersChange]);

  const handleSelectAll = (checked) => {
    const newSelectedOrders = new Set(selectedOrders);
    data.forEach(item => {
      if (checked) {
        newSelectedOrders.add(item.id);
      } else {
        newSelectedOrders.delete(item.id);
      }
    });
    
    setSelectedOrders(newSelectedOrders);
    setSelectAll(checked);
  };

  const handleOrderSelect = (orderId, isSelected) => {
    const newSelectedOrders = new Set(selectedOrders);
    
    if (isSelected) {
      newSelectedOrders.add(orderId);
    } else {
      newSelectedOrders.delete(orderId);
    }
    
    const currentPageSelected = data.every(item => 
      item.id === orderId ? isSelected : newSelectedOrders.has(item.id)
    );
    
    setSelectAll(currentPageSelected);
    setSelectedOrders(newSelectedOrders);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const handlePageSizeChange = (current, size) => {
    setItemsPerPage(size);
    setPage(1);
    localStorage.setItem("itemsPerPage", size.toString());
  };

  const darkMode = useDarkMode(false);
  const themeConfig = {
    token: {
      colorPrimary: "#1890ff",
      colorTextBase: darkMode.value ? "#ffffff" : "#000000",
      colorBgContainer: darkMode.value ? "#1f1f1f" : "#ffffff",
      colorBgElevated: darkMode.value ? "#2f2f2f" : "#ffffff",
      colorText: darkMode.value ? "#ffffff" : "#000000",
    },
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <div className={styles.market}>
        <Card className={styles.card}>
          <div className="overflow-auto">
            <div className={styles.table}>
              <div className={styles.row}>
                <div className={styles.col}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </div>
                <div className={cn("whitespace-nowrap flex items-center", styles.col)}>
                  Order Number
                </div>
                <div className={cn("whitespace-nowrap flex items-center", styles.col)}>
                  Created At
                </div>
              </div>
              {data.map((item) => (
                <Row
                  key={item.id}
                  data={item}
                  visibleColumns={["order_number", "created_at"]}
                  isSelected={selectedOrders.has(item.id)}
                  onSelect={(checked) => handleOrderSelect(item.id, checked)}
                />
              ))}
            </div>
          </div>

          <div className={styles.foot}>
            <Pagination
              current={page}
              total={totalItems}
              pageSize={itemsPerPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} orders`
              }
              showSizeChanger
            />
          </div>
        </Card>
      </div>
    </ConfigProvider>
  );
};

export default OrdersTable;